import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePageLoaderContext } from '../../contexts/PageLoaderContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirmService } from '../../services/confirmService';
import apiService from '../../services/api';
import { PaginatedApiResponse, User } from '../../types';
import NotificationContainer from '../Notification/NotificationContainer';
import ConfirmModal from '../Notification/ConfirmModal';
import CustomDropdown, { DropdownOption } from '../UI/CustomDropdown';
import SidePanel from '../UI/SidePanel';
import TabNavigation from './UserManagement/TabNavigation';
import UserTable from './UserManagement/UserTable';
import PendingRequestsTable from './UserManagement/PendingRequestsTable';
import PermissionGroupsSelector from './UserManagement/PermissionGroupsSelector';
import styles from '../../styles/UserManagement.module.css';
import buttonStyles from '../../styles/Buttons.module.css';
import searchStyles from '../../styles/SearchBar.module.css';
import sidePanelStyles from '../../styles/SidePanel.module.css';
import customDropdownStyles from '../../styles/CustomDropdown.module.css';

interface UserListResponse {
  status: string;
  data: User[];
  total: number;
  num_pages: number;
  message?: string;
}

interface FilterState {
  search: string;
  role: string;
  status: string;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();
  const confirmService = useConfirmService();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const isLoadingUsersRef = useRef(false); // Ref to track loading state without triggering re-renders
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    role: '',
    status: ''
  });

  // Pending Enrollments implementation
  const [activeTab, setActiveTab] = useState<'users' | 'requests'>('users');
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);

  // Dropdown options
  const roleOptions: DropdownOption[] = [
    { value: '', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'member', label: 'Member' }
  ];

  const statusOptions: DropdownOption[] = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  // Form dropdown options
  const formStatusOptions: DropdownOption[] = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const formDesignationOptions: DropdownOption[] = [
    { value: 'Chairman', label: 'Chairman' },
    { value: 'Vice_Chairman', label: 'Vice Chairman' },
    { value: 'director', label: 'Director' },
    { value: 'principal', label: 'Principal' },
    { value: 'vice_principal', label: 'Vice Principal' },
    { value: 'Dean', label: 'Dean' },
    { value: 'head_of_department', label: 'Head of Department' },
    { value: 'professor', label: 'Professor' },
    { value: 'associate_professor', label: 'Associate Professor' },
    { value: 'assistant_professor', label: 'Assistant Professor' },
    { value: 'Senior Assitant', label: 'Senior Assistant' },
    { value: 'Junior Assitant', label: 'Junior Assistant' },
    { value: 'grade1_technician', label: 'Grade 1 Technician' },
    { value: 'grade2_technician', label: 'Grade 2 Technician' }
  ];


  const USERS_PER_PAGE = 50;

  // Load Pending Requests
  // Load Pending Requests
  const loadRequests = useCallback(async () => {
    setIsLoadingRequests(true);
    try {
      const response = await apiService.getFacultyRequests();
      if ((response as any).requests) {
        setPendingRequests((response as any).requests);
      }
    } catch (error: any) {
      console.error("Error loading requests", error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || "Failed to load pending requests");
    } finally {
      setIsLoadingRequests(false);
    }
  }, [notificationService]);

  useEffect(() => {
    if (activeTab === 'requests') {
      loadRequests();
    }
  }, [activeTab, loadRequests]);

  const handleApproveRequest = async (requestId: string) => {
    const confirmed = await confirmService.show(
      'Approve Request',
      'Are you sure you want to approve this enrollment request?',
      { okText: 'Approve', cancelText: 'Cancel' }
    );
    if (!confirmed) return;

    showLoader('Approving request...'); // Optional: Remove if we want ONLY button loader
    // But since user asked for the button state, let's keep global loader OR rely on button state. 
    // Usually if a button has a loader, a global loader is distracting. 
    // I'll keep the global loader call commented out or active based on preference. 
    // The previous implementation had showLoader(). I will keep it for now as it provides a clear blocking state, 
    // but the task specifically asked for disabled state and loading animation (implied on button).
    // Let's rely on button state primarily for better UX if the global loader is blocking everything.
    // However, hiding the global loader might be better. Let's commented out showLoader for these actions.

    // showLoader('Approving request...'); 
    setActionLoadingId(requestId);
    setActionType('approve');

    try {
      await apiService.manageFacultyRequest(requestId, 'approve');
      notificationService.success('Request approved successfully');
      loadRequests();
      loadUsers(1);
    } catch (error: any) {
      notificationService.error(error.response?.data?.error || 'Failed to approve request');
    } finally {
      // hideLoader();
      setActionLoadingId(null);
      setActionType(null);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    const confirmed = await confirmService.show(
      'Reject Request',
      'Are you sure you want to reject this enrollment request?',
      { okText: 'Reject', cancelText: 'Cancel' }
    );
    if (!confirmed) return;

    // showLoader('Rejecting request...');
    setActionLoadingId(requestId);
    setActionType('reject');

    try {
      await apiService.manageFacultyRequest(requestId, 'reject', 'Admin rejected');
      notificationService.success('Request rejected successfully');
      loadRequests();
    } catch (error: any) {
      notificationService.error(error.response?.data?.error || 'Failed to reject request');
    } finally {
      // hideLoader();
      setActionLoadingId(null);
      setActionType(null);
    }
  };

  // Form states
  const [addForm, setAddForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    status: 'active',
    designation: 'professor'
  });

  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    status: 'active',
    designation: ''
  });

  const [permissionGroups, setPermissionGroups] = useState<any[]>([]);
  const [loadingPermissionGroups, setLoadingPermissionGroups] = useState(false);
  const [selectedPermissionGroups, setSelectedPermissionGroups] = useState<string[]>([]);
  const [isEditingMainAdmin, setIsEditingMainAdmin] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);

  const loadUsers = useCallback(async (page: number = 1) => {
    if (isLoadingUsersRef.current) {
      return;
    }

    isLoadingUsersRef.current = true;
    // setIsLoadingUsers(true); // Removed
    setLoading(true);
    showLoader('Loading users...');
    try {
      const response: PaginatedApiResponse<User[]> = await apiService.getUsersList({
        page: page,
        per_page: USERS_PER_PAGE,
        search: filters.search,
        role: filters.role,
        status: filters.status
      });

      if (response.status === 'success') {
        setUsers(response.data || []);
        setTotalUsers(response.total || 0);
        setTotalPages(response.num_pages || 1);
        setCurrentPage(page);
      } else {
        notificationService.error(response.message || 'Failed to load users');
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      notificationService.error(error.response?.data?.message || 'Failed to load users. Please try again.');
    } finally {
      setLoading(false);
      // setIsLoadingUsers(false); // Removed
      isLoadingUsersRef.current = false;
      hideLoader();
    }
  }, [filters, notificationService, showLoader, hideLoader]);

  // Handle search
  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Handle user selection
  const handleUserSelect = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUserIds);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUserIds(newSelected);
  };

  // Handle select all
  // Handle select all
  /*
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(users.map(user => user.id));
      setSelectedUserIds(allIds);
    } else {
      setSelectedUserIds(new Set());
    }
  };
  */

  // Clear all selections

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedUserIds(new Set());
  };

  // Add new user
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isAddingUser) return;

    setIsAddingUser(true);
    showLoader('Creating user...');
    try {
      const response = await apiService.addUser({
        full_name: addForm.full_name,
        email: addForm.email,
        phone: addForm.phone,
        status: addForm.status,
        designation: addForm.designation
      });

      if (response.status === 'success') {
        notificationService.success('User created successfully');
        setShowAddModal(false);
        setAddForm({ full_name: '', email: '', phone: '', status: 'active', designation: 'professor' });
        loadUsers(currentPage);
      } else {
        notificationService.error(response.message || 'Failed to create user');
      }
    } catch (error: any) {
      console.error('Error adding user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create user. Please try again.';
      notificationService.error(errorMessage);
    } finally {
      setIsAddingUser(false);
      hideLoader();
    }
  };

  // Edit user
  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser || isUpdatingUser) return;

    setIsUpdatingUser(true);
    showLoader('Updating user...');
    try {
      const response = await apiService.updateUser({
        user_id: editingUser.id,
        full_name: editForm.full_name,
        email: editForm.email,
        phone: editForm.phone,
        status: editForm.status,
        designation: editForm.designation
      });

      if (response.status === 'success') {
        // Update permission groups only if not main_admin
        if (!isEditingMainAdmin) {
          try {
            await apiService.updateUserPermissionGroups(
              editingUser.id,
              Array.from(selectedPermissionGroups)
            );
          } catch (permError: any) {
            console.error('Error updating permission groups:', permError);
            const permErrorMessage = permError.response?.data?.message || 'Failed to update permission groups';
            notificationService.warning(`User updated but ${permErrorMessage}`);
          }
        }

        notificationService.success('User updated successfully');
        handleCloseEditModal();
        loadUsers(currentPage);
      } else {
        notificationService.error(response.message || 'Failed to update user');
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update user. Please try again.';
      notificationService.error(errorMessage);
    } finally {
      setIsUpdatingUser(false);
      hideLoader();
    }
  };

  // Delete user
  const handleDeleteUser = async (userId: string) => {
    const confirmed = await confirmService.show(
      'Delete User',
      'Are you sure you want to delete this user? This action cannot be undone.',
      { okText: 'Delete', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    try {
      const response = await apiService.deleteUser(userId);

      if (response.status === 'success') {
        notificationService.success('User deleted successfully');
        loadUsers(currentPage);
      } else {
        notificationService.error(response.message || 'Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete user. Please try again.';
      notificationService.error(errorMessage);
    }
  };

  // Activate/Deactivate user
  const handleToggleUserStatus = async (userId: string, isActive: boolean) => {
    try {
      const response = isActive
        ? await apiService.deactivateUser(userId)
        : await apiService.activateUser(userId);

      if (response.status === 'success') {
        notificationService.success(`User ${isActive ? 'deactivated' : 'activated'} successfully`);
        loadUsers(currentPage);
      } else {
        notificationService.error(response.message || `Failed to ${isActive ? 'deactivate' : 'activate'} user`);
      }
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${isActive ? 'deactivate' : 'activate'} user. Please try again.`;
      notificationService.error(errorMessage);
    }
  };

  // Bulk operations
  const handleBulkOperation = async (operation: string) => {
    if (selectedUserIds.size === 0) return;

    const operationTitles = {
      'activate': 'Activate Users',
      'deactivate': 'Deactivate Users',
      'delete': 'Delete Users',
      'reset-passwords': 'Reset Passwords'
    };

    const operationMessages = {
      'activate': `Are you sure you want to activate ${selectedUserIds.size} users?`,
      'deactivate': `Are you sure you want to deactivate ${selectedUserIds.size} users?`,
      'delete': `Are you sure you want to delete ${selectedUserIds.size} users? This action cannot be undone.`,
      'reset-passwords': `Are you sure you want to reset passwords for ${selectedUserIds.size} users?`
    };

    const confirmed = await confirmService.show(
      operationTitles[operation as keyof typeof operationTitles],
      operationMessages[operation as keyof typeof operationMessages],
      { okText: operation.charAt(0).toUpperCase() + operation.slice(1), cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    try {
      const userIds = Array.from(selectedUserIds);
      let response;

      switch (operation) {
        case 'activate':
          response = await apiService.bulkActivateUsers(userIds);
          break;
        case 'deactivate':
          response = await apiService.bulkDeactivateUsers(userIds);
          break;
        case 'delete':
          response = await apiService.bulkDeleteUsers(userIds);
          break;
        case 'reset-passwords':
          response = await apiService.bulkResetPasswords(userIds);
          break;
        default:
          throw new Error(`Unknown operation: ${operation}`);
      }

      if (response.status === 'success') {
        notificationService.success(response.message || `${operation} completed successfully`);
        loadUsers(currentPage);
      } else {
        notificationService.error(response.message || `Failed to ${operation} users`);
      }
    } catch (error: any) {
      console.error(`Error performing bulk ${operation}:`, error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${operation} users. Please try again.`;
      notificationService.error(errorMessage);
    } finally {
      clearAllSelections();
    }
  };

  const handleExportUsers = async () => {
    try {
      const blob = await apiService.exportUsersExcel({
        search: filters.search,
        role: filters.role,
        status: filters.status
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      notificationService.success('Users exported successfully');
    } catch (error: any) {
      console.error('Error exporting users:', error);
      notificationService.error(error.response?.data?.message || 'Failed to export users. Please try again.');
    }
  };
  const handleFileSelect = (file: File) => {
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      notificationService.error('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }
    setImportFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleImportUsers = async () => {
    if (!importFile) {
      notificationService.error('Please select a file to import');
      return;
    }

    showLoader('Importing users...');
    try {
      const response = await apiService.importUsersExcel(importFile);

      if (response.status === 'success') {
        const message = response.message || 'Users imported successfully';
        const successCount = (response as any).success_count || 0;
        const errorCount = (response as any).error_count || 0;

        let fullMessage = `${message}`;
        if (successCount > 0) {
          fullMessage += ` ${successCount} user(s) created.`;
        }
        if (errorCount > 0 && (response as any).errors && (response as any).errors.length > 0) {
          fullMessage += ` ${errorCount} error(s) occurred.`;
          console.warn('Import errors:', (response as any).errors);
        }

        notificationService.success(fullMessage);
        setShowImportModal(false);
        setImportFile(null);
        loadUsers(currentPage);
      } else {
        notificationService.error(response.message || 'Failed to import users');
      }
    } catch (error: any) {
      console.error('Error importing users:', error);
      notificationService.error(error.response?.data?.message || 'Failed to import users. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await apiService.downloadUsersImportSample();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_import_sample.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      notificationService.success('Sample Excel file downloaded');
    } catch (error: any) {
      console.error('Error downloading sample:', error);
      notificationService.error(error.response?.data?.message || 'Failed to download sample file');
    }
  };
  const openEditModal = async (user: User) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.get_full_name || `${user.first_name} ${user.last_name}`,
      email: user.email,
      phone: user.mobile_number || '',
      status: user.is_active ? 'active' : 'inactive',
      designation: user.designation || 'professor'
    });
    setShowEditModal(true);
    await loadPermissionGroups();
    await loadUserPermissionGroups(user.id);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setSelectedPermissionGroups([]);
    setIsEditingMainAdmin(false);
  };

  const loadUserPermissionGroups = async (userId: string) => {
    try {
      const response = await apiService.getUserPermissionGroups(userId);
      if (response.status === 'success') {
        const groupIds = (response as any).permission_group_ids || [];
        setSelectedPermissionGroups(Array.isArray(groupIds) ? groupIds : []);
        setIsEditingMainAdmin((response as any).is_main_admin || false);
      }
    } catch (error) {
      console.error('Error loading user permission groups:', error);
      // If error, initialize with empty array
      setSelectedPermissionGroups([]);
      setIsEditingMainAdmin(false);
    }
  };

  const loadPermissionGroups = async () => {
    setLoadingPermissionGroups(true);
    try {
      const response = await apiService.getPermissionGroups({ is_active: 'true' });
      if (response.status === 'success') {
        setPermissionGroups((response as any).permission_groups || []);
      }
    } catch (error: any) {
      console.error('Error loading permission groups:', error);
      notificationService.error(error.response?.data?.message || 'Failed to load permission groups');
      setLoadingPermissionGroups(false);
    }
  };

  const togglePermissionGroup = (groupId: string) => {
    setSelectedPermissionGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadUsers(1);
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.role, filters.status]);

  // const formatDate = (dateString?: string) => {
  //   if (!dateString) return 'Never';
  //   return new Date(dateString).toLocaleDateString();
  // };

  const getUserName = (user: User) => {
    return user.get_full_name || `${user.first_name} ${user.last_name}`.trim();
  };

  const formatDesignation = (designation?: string) => {
    if (!designation) return 'N/A';
    return designation
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const isAllSelected = users.length > 0 && users.every(user => selectedUserIds.has(user.id));
  const isIndeterminate = selectedUserIds.size > 0 && selectedUserIds.size < users.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(users.map(u => u.id));
      setSelectedUserIds(allIds);
    } else {
      setSelectedUserIds(new Set());
    }
  };



  const handleTabChange = (tab: 'users' | 'requests') => {
    setActiveTab(tab);
    clearAllSelections();
  };

  return (
    <div className={styles.userManagementContainer || 'user-management-container'}>
      {/* Notification Container */}
      <NotificationContainer
        notifications={notificationService.notifications}
        onRemove={notificationService.remove}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmService.isOpen}
        title={confirmService.title}
        message={confirmService.message}
        okText={confirmService.options.okText}
        cancelText={confirmService.options.cancelText}
        onConfirm={confirmService.onConfirm}
        onCancel={confirmService.onCancel}
      />

      {/* Fixed Header - Combined Search, Filters, Actions, and Selection Banner */}
      <div className={`${styles.fixedHeader} ${selectedUserIds.size > 0 ? styles.withSelectionBanner : ''}`}>
        <div className={styles.headerContent}>
          {/* Tabs */}
          <TabNavigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {/* Single Row: Title, Search, Filters, and Actions */}
          {activeTab === 'users' && (
            <div className={styles.headerRow} style={{ marginTop: '1rem' }}>
              <div className={styles.searchWrapper}>
                <div className={searchStyles.searchBar}>
                  <input
                    type="text"
                    placeholder="Search by name, email, or ID"
                    value={filters.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <button type="submit">
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  </button>
                </div>
              </div>

              <div className={styles.headerActions}>
                <CustomDropdown
                  options={roleOptions}
                  value={filters.role}
                  onChange={(value) => handleFilterChange('role', value as string)}
                  placeholder="All Roles"
                  size="sm"
                  variant="outlined"
                  className="filter"
                />
                <CustomDropdown
                  options={statusOptions}
                  value={filters.status}
                  onChange={(value) => handleFilterChange('status', value as string)}
                  placeholder="All Status"
                  size="sm"
                  variant="outlined"
                  className="filter"
                />
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  onClick={() => setShowAddModal(true)}
                  title="Add New User"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14"></path>
                  </svg>
                  <span className={styles.btnText}>Add</span>
                </button>
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  onClick={handleExportUsers}
                  title="Export Excel"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                  </svg>
                  <span className={styles.btnText}>Export</span>
                </button>
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  onClick={() => setShowImportModal(true)}
                  title="Import Excel"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                    <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" />
                  </svg>
                  <span className={styles.btnText}>Import</span>
                </button>
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                  onClick={() => loadUsers(currentPage)}
                  title="Refresh"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 2v6h-6"></path>
                    <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                    <path d="M3 22v-6h6"></path>
                    <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                  </svg>
                  <span className={styles.btnText}>Refresh</span>
                </button>
              </div>
            </div>


          )}
        </div>
      </div>

      {/* Selection Banner */}
      {/* Content Section Wrapper with Margin */}
      <div className={`${activeTab === 'users' ? styles.marginTopLarge : styles.marginTopSmall}`} style={{ marginLeft: 'var(--space-xl)', marginRight: 'var(--space-xl)' }}>
        {
          selectedUserIds.size > 0 && (
            <div className={styles.selectionBanner} style={{ marginBottom: '16px' }}>
              <div className={styles.selectionBannerContent}>
                <span className={styles.selectionText}>Selected <strong>{selectedUserIds.size}</strong> users.</span>
                <div className={styles.selectionActions}>
                  <button
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondaryPrimary} ${styles.compactBtn} ${styles.tooltipBtn}`}
                    onClick={() => handleBulkOperation('activate')}
                    data-tooltip="Activate Selected"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <span className={styles.btnText}>Activate</span>
                  </button>
                  <button
                    className={`${buttonStyles.btn} ${buttonStyles.btnPrimary} ${styles.compactBtn} ${styles.tooltipBtn}`}
                    onClick={() => handleBulkOperation('reset-passwords')}
                    data-tooltip="Reset Passwords"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path fillRule="evenodd" d="M8 0a4 4 0 0 1 4 4v2.05a2.5 2.5 0 0 1 2 2.45v5a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 13.5v-5a2.5 2.5 0 0 1 2-2.45V4a4 4 0 0 1 4-4M4.5 7A1.5 1.5 0 0 0 3 8.5v5A1.5 1.5 0 0 0 4.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 11.5 7zM8 1a3 3 0 0 0-3 3v2h6V4a3 3 0 0 0-3-3" />
                    </svg>
                    <span className={styles.btnText}>Reset</span>
                  </button>
                  <button
                    className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${styles.compactBtn} ${styles.tooltipBtn}`}
                    onClick={() => handleBulkOperation('deactivate')}
                    data-tooltip="Deactivate Selected"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    <span className={styles.btnText}>Deactivate</span>
                  </button>
                  <button
                    className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${styles.compactBtn} ${styles.tooltipBtn}`}
                    onClick={() => handleBulkOperation('delete')}
                    data-tooltip="Delete Selected"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3,6 5,6 21,6"></polyline>
                      <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                    <span className={styles.btnText}>Delete</span>
                  </button>
                  <button
                    className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${styles.compactBtn} ${styles.tooltipBtn}`}
                    onClick={clearAllSelections}
                    data-tooltip="Clear Selection"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )
        }

        <div className={styles.userTableSection} style={{ marginTop: 0, marginLeft: 0, marginRight: 0, width: '100%' }}>

          {activeTab === 'requests' ? (
            <PendingRequestsTable
              loading={isLoadingRequests}
              requests={pendingRequests}
              onApprove={handleApproveRequest}
              onReject={handleRejectRequest}
              actionLoadingId={actionLoadingId}
              actionType={actionType}
            />
          ) : (
            <>
              <UserTable
                users={users}
                loading={loading}
                selectedUserIds={selectedUserIds}
                filters={filters}
                getUserName={getUserName}
                formatDesignation={formatDesignation}
                onUserSelect={handleUserSelect}
                isAllSelected={isAllSelected}
                isIndeterminate={isIndeterminate}
                onSelectAll={handleSelectAll}
                onEdit={openEditModal}
                onToggleStatus={handleToggleUserStatus}
                onDelete={handleDeleteUser}
                onShowAdd={() => setShowAddModal(true)}
                onNavigatePerformance={(id) => navigate(`/user-management/users/${id}/performance`)}
              />

              {/* Table Footer with Pagination */}
              {!loading && users.length > 0 && (
                <div className={styles.tableFooter}>
                  <div className={styles.tableInfo}>
                    <span>{totalUsers}</span> users found
                  </div>
                  <div className={styles.paginationContainer}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        className={`${styles.paginationBtn} ${page === currentPage ? styles.active : ''}`}
                        onClick={() => loadUsers(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>



      {/* Add user modal etc ... */}
      < SidePanel
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New User"
        icon={
          < svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg >
        }
      >
        <form className={sidePanelStyles.form} onSubmit={handleAddUser}>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="addFullName">
              Full Name <span className={sidePanelStyles.required}>*</span>
            </label>
            <input
              className={sidePanelStyles.formControl}
              type="text"
              id="addFullName"
              value={addForm.full_name}
              placeholder='Full Name'
              onChange={(e) => setAddForm(prev => ({ ...prev, full_name: e.target.value }))}
              required
            />
          </div>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="addEmail">
              Email <span className={sidePanelStyles.required}>*</span>
            </label>
            <input
              className={sidePanelStyles.formControl}
              type="email"
              placeholder='Email'
              id="addEmail"
              value={addForm.email}
              onChange={(e) => setAddForm(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="addPhone">
              Mobile Number <span className={sidePanelStyles.required}>*</span>
            </label>
            <input
              className={sidePanelStyles.formControl}
              type="text"
              placeholder='Mobile Number'
              id="addPhone"
              value={addForm.phone}
              onChange={(e) => setAddForm(prev => ({ ...prev, phone: e.target.value }))}
              required
            />
          </div>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="addStatus">
              Status <span className={sidePanelStyles.required}>*</span>
            </label>
            <CustomDropdown
              options={formStatusOptions}
              value={addForm.status}
              onChange={(value) => setAddForm(prev => ({ ...prev, status: value as string }))}
              placeholder="Select Status"
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
            />
          </div>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="addDesignation">
              Designation <span className={sidePanelStyles.required}>*</span>
            </label>
            <CustomDropdown
              options={formDesignationOptions}
              value={addForm.designation}
              onChange={(value) => setAddForm(prev => ({ ...prev, designation: value as string }))}
              placeholder="Select Designation"
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
              searchable={true}
            />
          </div>
          <div className={sidePanelStyles.formActions}>
            <button
              type="button"
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
              onClick={() => setShowAddModal(false)}
              disabled={isAddingUser}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
              disabled={isAddingUser}
            >
              {isAddingUser ? (
                <>
                  <svg className={styles.spinner} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Creating...
                </>
              ) : (
                'Add User'
              )}
            </button>
          </div>
        </form>
      </SidePanel >

      {/* Edit User Side Panel */}
      < SidePanel
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        title="Edit User"
        icon={
          < svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg >
        }
      >
        <form className={sidePanelStyles.form} onSubmit={handleEditUser}>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="editFullName">
              Full Name <span className={sidePanelStyles.required}>*</span>
            </label>
            <input
              className={sidePanelStyles.formControl}
              type="text"
              id="editFullName"
              placeholder='Full Name'
              value={editForm.full_name}
              onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
              required
            />
          </div>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="editEmail">
              Email <span className={sidePanelStyles.required}>*</span>
            </label>
            <input
              className={sidePanelStyles.formControl}
              type="email"
              id="editEmail"
              placeholder='Email'
              value={editForm.email}
              onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="editPhone">
              Mobile Number <span className={sidePanelStyles.required}>*</span>
            </label>
            <input
              className={sidePanelStyles.formControl}
              type="text"
              id="editPhone"
              placeholder='Mobile Number'
              value={editForm.phone}
              onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
              required
            />
          </div>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="editStatus">
              Status <span className={sidePanelStyles.required}>*</span>
            </label>
            <CustomDropdown
              options={formStatusOptions}
              value={editForm.status}
              onChange={(value) => setEditForm(prev => ({ ...prev, status: value as string }))}
              placeholder="Select Status"
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
            />
          </div>
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel} htmlFor="editDesignation">
              Designation <span className={sidePanelStyles.required}>*</span>
            </label>
            <CustomDropdown
              options={formDesignationOptions}
              value={editForm.designation}
              onChange={(value) => setEditForm(prev => ({ ...prev, designation: value as string }))}
              placeholder="Select Designation"
              size="md"
              variant="outlined"
              className={customDropdownStyles.sizeFull}
              searchable={true}
            />
          </div>

          {/* Permissions Section - Only in Edit Mode */}
          <PermissionGroupsSelector
            isEditingMainAdmin={isEditingMainAdmin || false}
            loading={loadingPermissionGroups}
            permissionGroups={permissionGroups}
            selectedGroups={selectedPermissionGroups}
            onToggle={togglePermissionGroup}
          />

          <div className={sidePanelStyles.formActions}>
            <button
              type="button"
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
              onClick={handleCloseEditModal}
              disabled={isUpdatingUser}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
              disabled={isUpdatingUser}
            >
              {isUpdatingUser ? (
                <>
                  <svg className={styles.spinner} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </SidePanel >

      {/* Import Users Side Panel */}
      < SidePanel
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setImportFile(null);
          setIsDragging(false);
        }}
        title="Import Users from Excel"
        icon={
          < svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg >
        }
      >
        <div className={sidePanelStyles.form}>
          {/* File Upload Area */}
          <div className={sidePanelStyles.formGroup}>
            <div
              className={`${styles.uploadArea} ${isDragging ? styles.dragOver : ''} ${importFile ? styles.hasFile : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className={styles.uploadContent}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.uploadIcon}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                {importFile ? (
                  <>
                    <p className={styles.uploadText}>File selected: <strong>{importFile.name}</strong></p>
                    <button
                      type="button"
                      className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                      onClick={() => setImportFile(null)}
                      style={{ marginTop: '0.5rem' }}
                    >
                      Change File
                    </button>
                  </>
                ) : (
                  <>
                    <p className={styles.uploadText}>Drag & drop your Excel file here</p>
                    <p className={styles.uploadSubtext}>
                      or <label htmlFor="fileInput" className={styles.browseLink}>browse</label> to select a file
                    </p>
                    <input
                      type="file"
                      id="fileInput"
                      accept=".xlsx,.xls"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                    />
                    <p className={styles.fileFormatNote}>Accepted: .xlsx, .xls</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={sidePanelStyles.formActions} style={{ justifyContent: 'space-between', marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                onClick={handleDownloadSample}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.5rem' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Download Sample Excel
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setIsDragging(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                onClick={handleImportUsers}
                disabled={!importFile}
              >
                Upload & Import
              </button>
            </div>
          </div>

          {/* Excel Header Requirements */}
          <div className={styles.requirementsSection}>
            <h3 className={styles.requirementsTitle}>Excel Header Requirements</h3>
            <ol className={styles.requirementsList}>
              <li>
                <strong>Full Name</strong> <span className={sidePanelStyles.required}>(required)</span>
              </li>
              <li>
                <strong>Email</strong> <span className={sidePanelStyles.required}>(required)</span> - Must be unique
              </li>
              <li>
                <strong>Phone</strong> or <strong>Mobile Number</strong> <span className={sidePanelStyles.required}>(required)</span>
              </li>
              <li>
                <strong>Designation</strong> <span className={sidePanelStyles.required}>(required)</span> - Values: Chairman, Vice_Chairman, director, principal, etc. (Role is automatically assigned based on hierarchy)
              </li>
              <li>
                <strong>Status</strong> <span className={sidePanelStyles.required}>(required)</span> - Values: active, inactive
              </li>
            </ol>

          </div>
        </div>
      </SidePanel >
    </div >
  );
};

export default UserManagement;